import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { api, formatError } from "@/lib/api";
import { Crosshair } from "@/components/Crosshair";
import { toast } from "@/lib/toast";
import { Coins, ArrowUpToLine, Loader2, ArrowLeft, CheckCircle2, ArrowDownRight, ArrowUpRight } from "lucide-react";

const money = (cents) => `$${(cents / 100).toFixed(2)}`;

function actionLabel(e) {
  if (e.type === "topup") return e.action === "credit_pack" ? "Credit pack" : "Top-up / grant";
  if (e.action === "bump") return "Bump" + (e.listing_id ? "" : "");
  if (e.action === "post_fee") return "Extra listing fee";
  return e.action || e.type;
}

export default function BuyCredits() {
  const [data, setData] = useState(null);
  const [busyCents, setBusyCents] = useState(null);
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();

  const load = useCallback(() => api.get("/credits/balance").then(({ data }) => setData(data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  // Return from Stripe Checkout — confirm the pack, then clean the URL.
  useEffect(() => {
    const status = params.get("status");
    const sid = params.get("session_id");
    if (status === "success" && sid) {
      api.get(`/credits/status/${sid}`).then(({ data }) => {
        if (data.status === "completed") toast.success("Credits added to your balance.");
        else toast("Payment received — your balance will update momentarily.");
        load();
      }).catch(() => load()).finally(() => setParams({}, { replace: true }));
    } else if (status === "cancel") {
      toast("Checkout cancelled — no charge made.");
      setParams({}, { replace: true });
    }
  }, [params, setParams, load]);

  const buy = async (cents) => {
    setBusyCents(cents);
    try {
      const { data } = await api.post("/credits/checkout", { cents, origin_url: window.location.origin });
      if (data.url) window.location.assign(data.url);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
      setBusyCents(null);
    }
  };

  if (!data) return <div className="min-h-screen text-foreground"><Navbar /><div className="text-center py-20 font-mono uppercase text-muted-foreground">Loading…</div></div>;

  return (
    <div className="min-h-screen text-foreground" data-testid="buy-credits-page">
      <Navbar />
      <div className="max-w-3xl mx-auto px-4 py-8">
        <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1.5 text-xs font-mono uppercase text-muted-foreground hover:text-foreground mb-2">
          <ArrowLeft className="w-3.5 h-3.5" /> Back
        </button>
        <div className="flex items-center gap-2">
          <Coins className="w-6 h-6 text-volt" />
          <h1 className="font-heading text-4xl font-bold uppercase tracking-tight">Credits</h1>
        </div>
        <p className="text-sm text-secondary-foreground font-body mt-1">Prepaid credits power bumps and extra listings. Top up once — no card charge per bump.</p>

        <div className="mt-5 border border-sniper bg-surface p-5 flex items-end justify-between" data-testid="credit-balance">
          <div>
            <div className="font-mono text-[10px] uppercase text-muted-foreground">Current balance</div>
            <div className="font-heading text-5xl font-bold text-volt">{money(data.balance_cents)}</div>
          </div>
          <div className="text-right font-mono text-xs text-secondary-foreground">
            <div className="inline-flex items-center gap-1.5"><ArrowUpToLine className="w-3.5 h-3.5 text-olive-bright" /> ≈ {Math.floor(data.balance_cents / Math.round(data.bump_fee * 100))} bumps left</div>
            <div className="text-muted-foreground mt-0.5">Bump = {money(Math.round(data.bump_fee * 100))}</div>
          </div>
        </div>

        <Crosshair className="my-6" />

        {data.purchase_enabled ? (
          <>
            <h2 className="font-heading text-lg uppercase tracking-tight">Add credits</h2>
            <div className="mt-3 grid sm:grid-cols-3 gap-3">
              {data.packs.map((p) => (
                <div key={p.cents} className="border border-sniper hover:border-olive bg-surface p-4 transition-colors" data-testid={`credit-pack-${p.cents}`}>
                  <div className="font-heading text-3xl font-bold text-foreground">{money(p.cents)}</div>
                  <div className="font-mono text-[11px] uppercase text-olive-bright mt-1 inline-flex items-center gap-1.5"><ArrowUpToLine className="w-3.5 h-3.5" /> {p.bumps} bumps</div>
                  <button onClick={() => buy(p.cents)} disabled={busyCents !== null} data-testid={`buy-pack-${p.cents}`}
                    className="mt-3 w-full inline-flex items-center justify-center gap-2 bg-volt hover:brightness-110 disabled:opacity-50 text-black font-heading font-bold uppercase text-sm py-2.5 transition-all">
                    {busyCents === p.cents ? <><Loader2 className="w-4 h-4 animate-spin" /> Opening…</> : "Buy"}
                  </button>
                </div>
              ))}
            </div>
            <p className="font-mono text-[10px] uppercase text-muted-foreground mt-2">Secure checkout by Stripe. Credits never expire.</p>
          </>
        ) : (
          <div className="border border-sniper bg-surface p-6 text-center" data-testid="credits-purchase-disabled">
            <h2 className="font-heading text-lg uppercase tracking-tight text-foreground">Credit purchases are coming soon</h2>
            <p className="text-sm text-secondary-foreground font-body mt-1.5 max-w-md mx-auto">Buying credit packs isn't available yet — we're finalizing the terms. Your existing balance and history stay right here.</p>
          </div>
        )}

        <Crosshair className="my-6" />

        <h2 className="font-heading text-lg uppercase tracking-tight">History</h2>
        <div className="mt-3 border border-sniper divide-y divide-sniper" data-testid="credit-history">
          {data.history.length === 0 && <div className="p-4 font-mono text-xs text-muted-foreground uppercase">No credit activity yet.</div>}
          {data.history.map((e, i) => {
            const credit = e.amount_cents > 0;
            return (
              <div key={e.entry_id || i} className="flex items-center justify-between px-4 py-3">
                <div className="flex items-center gap-2.5 min-w-0">
                  {credit ? <ArrowUpRight className="w-4 h-4 text-olive-bright shrink-0" /> : <ArrowDownRight className="w-4 h-4 text-blaze shrink-0" />}
                  <div className="min-w-0">
                    <div className="font-mono text-xs uppercase text-foreground truncate">{actionLabel(e)}</div>
                    <div className="font-mono text-[10px] text-muted-foreground">{(e.created_at || "").slice(0, 10)}</div>
                  </div>
                </div>
                <div className={`font-mono text-sm ${credit ? "text-olive-bright" : "text-blaze"}`}>{credit ? "+" : "−"}{money(Math.abs(e.amount_cents))}</div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
